import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, ArrowLeft, Calendar, Mail } from "lucide-react";

export default function TermosUso() {
  return (
    <div className="min-h-screen bg-hero-radial bg-white pb-12">
      <div className="max-w-3xl mx-auto px-5 pt-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={32} />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Link>
          </Button>
        </div>

        <Card className="rounded-2xl">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-semibold text-[hsl(var(--primary))] m-0">Termos de Uso</h1>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3" /> Última atualização: 14/05/2026
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              Bem-vindo(a) ao <strong>CEAP BRASIL</strong>. Ao acessar e utilizar nossa plataforma (aplicativo móvel e web), você concorda com estes Termos de Uso. <strong>Leia com atenção</strong>.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">1. Objeto</h2>
            <p className="text-sm text-muted-foreground">
              Esta plataforma é oferecida pelo <strong>CEAP — Centro de Estudos da Administração Pública</strong>, e destina-se a alunos, ex-alunos e potenciais alunos dos cursos do CEAP, com foco em <strong>Legislativo Municipal</strong> e <strong>Licitações Públicas</strong>. Permite acesso a carteira digital, registro de presença em cursos via QR Code, emissão de certificados digitais, cursos gratuitos online, materiais didáticos, banco de ideias (Banco de PLs), modelos técnicos, convênios e gamificação por níveis.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">2. Cadastro e Acesso</h2>
            <ul className="text-sm text-muted-foreground list-disc ml-5 mt-2 space-y-1">
              <li>O cadastro é gratuito e exige fornecimento de dados verdadeiros e atualizados</li>
              <li>Você é responsável pela guarda da sua senha e por todas as ações realizadas em sua conta</li>
              <li>É proibido compartilhar credenciais com terceiros</li>
              <li>Em caso de uso indevido ou suspeita de violação, comunique-nos imediatamente pelo e-mail abaixo</li>
            </ul>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">3. Uso Permitido</h2>
            <p className="text-sm text-muted-foreground">Ao utilizar o CEAP BRASIL, você se compromete a:</p>
            <ul className="text-sm text-muted-foreground list-disc ml-5 mt-2 space-y-1">
              <li>Utilizar a plataforma exclusivamente para fins educacionais e legais</li>
              <li>Não fraudar registros de presença (uso do QR Code é pessoal e intransferível)</li>
              <li>Não tentar acessar áreas restritas ou contas de outros usuários</li>
              <li>Não realizar engenharia reversa, descompilação ou cópia não autorizada do aplicativo</li>
              <li>Não publicar, transmitir ou compartilhar conteúdo ilegal, ofensivo, difamatório ou que viole direitos de terceiros</li>
              <li>Respeitar os direitos autorais sobre materiais didáticos disponibilizados</li>
            </ul>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">4. Carteira Digital e QR Code</h2>
            <p className="text-sm text-muted-foreground">
              A carteira digital identifica o aluno como vinculado ao CEAP. O QR Code é gerado de forma única, com renovação automática a cada poucos minutos (anti-fraude). É <strong>vedada</strong> a apresentação do QR de outra pessoa ou o uso de capturas de tela antigas — tais práticas podem resultar em <strong>bloqueio da conta</strong> e perda de certificados já emitidos.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">5. Certificados</h2>
            <ul className="text-sm text-muted-foreground list-disc ml-5 mt-2 space-y-1">
              <li>O certificado é liberado quando o aluno soma <strong>3,0 créditos</strong> de presença no curso. Cada período (combinação de <strong>dia + turno</strong>) vale <strong>1,0 crédito</strong> na entrada pontual (manhã até 10:30 / tarde até 15:00) ou <strong>0,5 crédito (meia entrada)</strong> na entrada atrasada. A saída é automática (manhã às 12h, tarde às 17h) e a frequência considera apenas o horário de entrada</li>
              <li>Mesmo com os 3,0 créditos concluídos, o certificado só pode ser <strong>acessado/baixado após a resposta à Pesquisa de Satisfação</strong> do curso</li>
              <li>Cada certificado possui código único de validação pública</li>
              <li>O CEAP se reserva o direito de revogar certificados emitidos com base em informações fraudulentas</li>
            </ul>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">6. Pesquisa de Satisfação</h2>
            <p className="text-sm text-muted-foreground">
              Ao final de cada curso, o aluno deve responder a Pesquisa de Satisfação para liberação do certificado. As respostas são utilizadas para melhoria contínua. Depoimentos podem ser utilizados <strong>de forma anônima</strong> em materiais de divulgação, salvo solicitação expressa em contrário.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">7. Convênios e Benefícios</h2>
            <p className="text-sm text-muted-foreground">
              Convênios e descontos disponibilizados são fornecidos por parceiros terceiros. O CEAP atua como facilitador e <strong>não se responsabiliza</strong> pela qualidade, prazo ou condições oferecidas pelos parceiros. Quaisquer reclamações devem ser direcionadas ao parceiro responsável.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">8. Propriedade Intelectual</h2>
            <p className="text-sm text-muted-foreground">
              Todo o conteúdo da plataforma (textos, layout, marca, código, materiais didáticos, vídeos) é de propriedade do CEAP ou de seus licenciadores, protegido por leis de direitos autorais. É <strong>vedada</strong> a reprodução, distribuição ou modificação sem autorização expressa.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">9. Suspensão e Cancelamento</h2>
            <p className="text-sm text-muted-foreground">
              O CEAP pode suspender ou cancelar contas que violem estes Termos, sem aviso prévio nos casos graves. Você pode solicitar o cancelamento da sua conta a qualquer momento pelo e-mail abaixo, observado o disposto na Política de Privacidade.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">10. Limitação de Responsabilidade</h2>
            <p className="text-sm text-muted-foreground">
              A plataforma é oferecida "no estado em que se encontra". Apesar de empenharmos os melhores esforços, não garantimos disponibilidade ininterrupta ou ausência de erros. O CEAP não se responsabiliza por danos indiretos, lucros cessantes ou perda de dados decorrentes do uso da plataforma, salvo nos casos previstos em lei.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">11. Alterações dos Termos</h2>
            <p className="text-sm text-muted-foreground">
              Estes Termos podem ser atualizados periodicamente. Alterações relevantes serão comunicadas por e-mail ou aviso no aplicativo, com 15 dias de antecedência. O uso continuado após as mudanças significa aceitação.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">12. Foro</h2>
            <p className="text-sm text-muted-foreground">
              Fica eleito o foro da comarca da sede do CEAP para dirimir quaisquer controvérsias decorrentes destes Termos, com renúncia a qualquer outro, por mais privilegiado que seja.
            </p>

            <div className="mt-8 p-4 rounded-lg bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))]">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-semibold text-sm text-[hsl(var(--primary))]">Contato</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Dúvidas sobre estes Termos? Escreva para:<br />
                    <a href="mailto:administrativo@ceapbrasil.com" className="text-[hsl(var(--accent))] hover:underline">administrativo@ceapbrasil.com</a>
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center mt-8">
              CEAP BRASIL — Centro de Estudos da Administração Pública<br />
              Veja também nossa <Link to="/politica-privacidade" className="text-[hsl(var(--accent))] hover:underline">Política de Privacidade</Link>.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
